/**
 * LocalStorage Manager für Projektdaten
 * 
 * Speichert Projektdaten, Fotos und Analyseergebnisse lokal
 * für Offline-Fähigkeiten und Performance
 */

export interface StoredMeasurementEntry {
  photoId: string;
  /** Neues Datenmodell: Mengenfeld + Einheit. */
  amount?: number;
  amountFormula?: string;
  unit?: string;
  mengen: Array<{ id: string; label: string; unit: string; value: number; isManual: boolean }>;
  scale: number;
  /** @deprecated Legacy — wird beim Hydrieren noch gelesen, aber nicht mehr neu geschrieben. */
  netArea?: number;
  /** @deprecated Legacy. */
  totalArea?: number;
  /** @deprecated Legacy. */
  netAreaOverride?: number;
  /** @deprecated Legacy. */
  openingCount?: number;
  /** @deprecated Legacy. */
  netAreaFormula?: string;
  /** @deprecated Legacy. */
  totalAreaFormula?: string;
}

export interface StoredLvState {
  mengen: Record<string, number>;
  mengenRefs: Record<string, { mengeId: string; imageIndex: number; sourceLabel: string }>;
  /**
   * IDs of LV-Positionen, deren Menge weiterhin automatisch aus der
   * Aufmaß-Gesamtfläche (Summe aller netAreas) gespeist wird. Sobald der Nutzer
   * die Menge einer dieser Positionen manuell ändert, wird die ID entfernt.
   */
  autoFilledPositionIds?: string[];
  /**
   * Aus Per-Foto-Daten injizierte zusätzliche Einheits-Varianten der
   * Stamm-Positionen (z.B. dieselbe Position einmal in m² und einmal in lfm).
   * Werden beim LV-Mount als eigenständige Zeilen eingefügt und zählen normal
   * zur Summe.
   */
  extras?: Array<{ baseId: string; unit: string; ep: number; qty: number }>;
  /**
   * EP-Overrides für Stamm-Positionen (mengen-gewichteter Durchschnitt aus
   * Per-Foto-Daten). Wird beim LV-Mount via initialEpOverrides angewendet.
   */
  epOverrides?: Record<string, number>;
  /**
   * Vom Nutzer im Projekt händisch angelegte ("custom-") Positionen mit
   * vollständigen Stammdaten (Bezeichnung, Einheit, EP, Menge, Sektion).
   * Werden beim LV-Mount in die jeweilige Sektion injiziert, sodass
   * Bezeichnung/Einheit/EP nach einem Reload erhalten bleiben — anders als
   * `mengen`, das nur die Menge pro ID kennt.
   */
  customPositionen?: Array<{
    id: string;
    sektionId: string;
    name: string;
    unit: string;
    ep: number;
    qty: number;
    aktiv: boolean;
    tag?: string | null;
  }>;
  /**
   * Position-IDs (insb. `lib-…`), deren Einzelpreis der Nutzer manuell
   * überschrieben hat. Für solche IDs wird die mengenabhängige
   * Bibliotheks-EP-Auswahl unterdrückt, damit ein bewusster manueller Preis
   * nicht ungewollt überschrieben wird.
   */
  manuelleEps?: string[];
}

export interface StoredProject {
  id: string;
  name: string;
  customer: string;
  address: string;
  description?: string;
  photos: StoredPhoto[];
  analysisResults: StoredAnalysis[];
  quoteData: StoredQuote;
  lastModified: string;
  status: 'draft' | 'active' | 'completed';
  measurementData?: StoredMeasurementEntry[];
  lvState?: StoredLvState;
  /** True, sobald die Legacy-Foto-Migration für dieses Projekt vollständig
   *  erfolgreich durchgelaufen ist. Verhindert wiederholte Saves. */
  legacyPhotoMigrationCompleted?: boolean;
}

export interface PhotoLVPosition {
  id: string;
  name: string;
  unit: string;
  ep: number;
  /** Vom Nutzer bestätigte / übernommene Menge (verbindlich für LV). */
  qty: number;
  /** Aus Aufmaß automatisch berechneter Vorschlag (optional, nur informativ). */
  berechneteMenge?: number;
  /** True = nur Vergleichs-/Variantenwert; zählt NICHT zur Foto-Zwischensumme und wird NICHT ins LV übernommen. */
  variant?: boolean;
}

export interface StoredPhoto {
  /** Stabile Foto-ID — Server-Datenbank-ID als String. */
  id: string;
  fileName: string;
  fileSize: number;
  mimeType?: string;
  uploadDate: string;
  description?: string;
  photoPositionen?: PhotoLVPosition[];
  /**
   * Nur noch zur einmaligen Migration verwendet. Neue Fotos
   * werden auf dem Server gespeichert und enthalten dieses Feld nicht.
   */
  dataUrl?: string;
}

export interface StoredAnalysis {
  photoId: string;
  material: string;
  condition: string;
  damage: string[];
  recommendations: string[];
  confidence: number;
  analysisDate: string;
}

export interface StoredQuote {
  items: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    total: number;
  }>;
  notes: string;
  validUntil: string;
  subtotal: number;
  tax: number;
  total: number;
  createdDate: string;
}

/**
 * Persistierter Angebotsstand für die Quote-Komponente.
 * Enthält ausschließlich die TATSÄCHLICH editierbaren Felder.
 * Abgeleitete Items (LV-Positionen, Foto-Aggregate) werden NIE hier gespeichert,
 * sie kommen aus den eigenständig persistierten Quellen (lvState / Foto-Daten).
 */
export interface StoredQuoteManualItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
}

export interface StoredQuoteData {
  schemaVersion: 1;
  customerData: { name: string; address: string; email: string };
  addressManuallyEdited: boolean;
  notes: string;
  validUntil: string;
  selectedPhotoIds: string[];
  manualItems: StoredQuoteManualItem[];
  quoteNumber: string;
  updatedAt: string;
  /**
   * Archivierte frühere Angebots-Versionen. Wird befüllt, wenn ein bereits
   * abgeschlossenes Angebot nachträglich geändert wird: der letzte persistierte
   * Stand wird als Version eingefroren und die Angebotsnummer hochgezählt
   * (z.B. "BP-2026-0048" → "BP-2026-0048-V2").
   */
  versions?: StoredQuoteVersion[];
}

export interface StoredQuoteVersion {
  quoteNumber: string;
  archivedAt: string;
  snapshot: Omit<StoredQuoteData, 'versions'>;
  /**
   * Eingefrorene komplette Positionsliste (inkl. LV- und Foto-Positionen)
   * zum Archivierungszeitpunkt. Ohne diesen Freeze würden spätere Änderungen
   * am LV die historische Version rückwirkend verändern.
   */
  frozenItems?: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    total: number;
    variant?: boolean;
    /** Herkunfts-Hinweis (z.B. Foto-Label bei Positionen aus der Foto-Aufschlüsselung). */
    sourceLabel?: string;
  }>;
  /** Nettosumme der eingefrorenen Positionen (ohne Varianten), inkl. Foto-Aufschlüsselungs-Anteil. */
  frozenNetTotal?: number;
  /** Projektname zum Archivierungszeitpunkt (für den PDF-Export der Version). */
  frozenProjectName?: string;
  /** Projektbeschreibung/Stammdaten zum Archivierungszeitpunkt (für den PDF-Export der Version). */
  frozenProjectDetails?: {
    activity?: string;
    objektbeschaffenheit?: string[];
    massnahmen?: string[];
    geschosszahl?: number | null;
    hoehe?: number | null;
    menge?: number | null;
    mengeneinheit?: string;
    description?: string;
  };
}

export class LocalStorageManager {
  private static readonly PROJECTS_KEY = 'bauwerkplus_projects';
  private static readonly SETTINGS_KEY = 'bauwerkplus_settings';
  private static readonly MAX_STORAGE_SIZE = 50 * 1024 * 1024; // 50MB Limit

  /**
   * Speichert Projekt in LocalStorage.
   *
   * Guard gegen destruktive Writes: Wenn der neue State weniger Fotos oder
   * weniger Messdaten als der bestehende enthält, wird der Write blockiert —
   * außer der Aufrufer setzt explizit `allowShrink: true` (z.B. beim
   * regulären Lösch-Pfad `removePhotoMetadata`).
   *
   * `quoteData.items` wird bewusst NICHT geprüft — Quote-Items sind regulär
   * über die UI editierbar und schrumpfen legitimerweise.
   */
  static saveProject(
    project: StoredProject,
    opts?: { allowShrink?: boolean; reason?: string },
  ): boolean {
    try {
      const projects = this.getAllProjects();
      const existingIndex = projects.findIndex(p => p.id === project.id);

      // ─── Destruktiv-Write-Guard ───
      if (existingIndex >= 0 && opts?.allowShrink !== true) {
        const prev = projects[existingIndex];
        const photoLoss = (prev.photos?.length ?? 0) - (project.photos?.length ?? 0);
        const measLoss =
          (prev.measurementData?.length ?? 0) - (project.measurementData?.length ?? 0);
        if (photoLoss > 0 || measLoss > 0) {
          console.error('[storage][BLOCKED destructive write]', {
            id: project.id,
            reason: opts?.reason,
            photoLoss,
            measLoss,
            prevPhotos: prev.photos?.length ?? 0,
            newPhotos: project.photos?.length ?? 0,
            prevMeas: prev.measurementData?.length ?? 0,
            newMeas: project.measurementData?.length ?? 0,
          });
          return false;
        }
      }

      project.lastModified = new Date().toISOString();

      if (existingIndex >= 0) {
        projects[existingIndex] = project;
      } else {
        projects.push(project);
      }

      const dataSize = JSON.stringify(projects).length;
      if (dataSize > this.MAX_STORAGE_SIZE) {
        // Wir DÜRFEN auf keinen Fall stillschweigend Projekte löschen — der Server
        // ist die Wahrheit, aber lokale Backups bleiben wichtig, falls die Server-
        // Verbindung gerade fehlt. Stattdessen nur warnen.
        console.warn(
          '[storage] LocalStorage-Limit erreicht. Projekte werden NICHT automatisch entfernt — ' +
          'bitte alte Projekte manuell löschen oder Cache-Export nutzen.'
        );
      }

      try {
        localStorage.setItem(this.PROJECTS_KEY, JSON.stringify(projects));
      } catch (writeErr) {
        const isQuota =
          writeErr instanceof DOMException &&
          (writeErr.name === 'QuotaExceededError' ||
            writeErr.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
            writeErr.code === 22 || writeErr.code === 1014);
        if (isQuota) {
          // KEINE Auto-Löschung. Nur UI-Eskalation mit Vorschlagsliste.
          const now = Date.now();
          const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
          const candidates = projects
            .filter(p => p.id !== project.id)
            .filter(p => {
              const ts = p.lastModified ? Date.parse(p.lastModified) : NaN;
              return Number.isFinite(ts) && now - ts > THIRTY_DAYS;
            })
            .map(p => ({ id: p.id, name: p.name, lastModified: p.lastModified }));
          try {
            window.dispatchEvent(
              new CustomEvent('storage-quota-exceeded', {
                detail: { projectId: project.id, candidates },
              }),
            );
          } catch {}
          console.error('[storage][QUOTA EXCEEDED]', {
            projectId: project.id,
            candidates: candidates.length,
          });
          return false;
        }
        throw writeErr;
      }
      console.log(`Projekt "${project.name}" erfolgreich gespeichert`);
      return true;

    } catch (error) {
      console.error('Fehler beim Speichern des Projekts:', error);
      return false;
    }
  }

  /**
   * Lädt Projekt aus LocalStorage
   */
  static getProject(projectId: string): StoredProject | null {
    try {
      const projects = this.getAllProjects();
      return projects.find(p => p.id === projectId) || null;
    } catch (error) {
      console.error('Fehler beim Laden des Projekts:', error);
      return null;
    }
  }

  /**
   * Lädt alle Projekte aus LocalStorage
   */
  static getAllProjects(): StoredProject[] {
    try {
      const data = localStorage.getItem(this.PROJECTS_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Fehler beim Laden der Projekte:', error);
      return [];
    }
  }

  /**
   * Löscht Projekt aus LocalStorage
   */
  static deleteProject(projectId: string): boolean {
    try {
      const projects = this.getAllProjects();
      const filteredProjects = projects.filter(p => p.id !== projectId);
      localStorage.setItem(this.PROJECTS_KEY, JSON.stringify(filteredProjects));
      console.log('Projekt erfolgreich gelöscht');
      return true;
    } catch (error) {
      console.error('Fehler beim Löschen des Projekts:', error);
      return false;
    }
  }

  /**
   * Stellt sicher, dass ein Projekt in LocalStorage existiert (erstellt minimalen Eintrag falls nötig)
   */
  static ensureProject(projectId: string, name: string = 'Projekt'): void {
    const existing = this.getProject(projectId);
    if (!existing) {
      const minimal: StoredProject = {
        id: projectId,
        name,
        customer: '',
        address: '',
        photos: [],
        analysisResults: [],
        quoteData: {
          items: [],
          notes: '',
          validUntil: '',
          subtotal: 0,
          tax: 0,
          total: 0,
          createdDate: new Date().toISOString(),
        },
        lastModified: new Date().toISOString(),
        status: 'draft',
      };
      this.saveProject(minimal);
    }
  }

  /**
   * Aktualisiert description und photoPositionen eines gespeicherten Fotos
   */
  static updatePhotoData(
    projectId: string,
    photoId: string,
    fields: { description?: string; photoPositionen?: PhotoLVPosition[]; fileName?: string }
  ): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) return false;
      const photo = project.photos.find((p) => p.id === photoId);
      if (!photo) return false;
      if (fields.fileName !== undefined) photo.fileName = fields.fileName;
      if (fields.description !== undefined) photo.description = fields.description;
      if (fields.photoPositionen !== undefined) photo.photoPositionen = fields.photoPositionen;
      return this.saveProject(project);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Fotodaten:', error);
      return false;
    }
  }

  /**
   * Speichert Aufmaßdaten (Messergebnisse) eines Fotos
   */
  static saveMeasurementData(projectId: string, entry: StoredMeasurementEntry): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) return false;
      if (!project.measurementData) project.measurementData = [];

      // Vor-Filter (photoId-spezifisch): Wenn der bestehende Eintrag für DIESES
      // Foto bereits Werte/Formeln hatte und der neue Schreibvorgang ALLES auf
      // leer zurücksetzt → vermutlich Hydration-/Reset-Artefakt, blockieren.
      const prevEntry = project.measurementData.find(m => m.photoId === entry.photoId);
      const prevHadValues = !!prevEntry && (
        (prevEntry.amount ?? 0) > 0 ||
        !!prevEntry.amountFormula ||
        (prevEntry.netArea ?? 0) > 0 ||
        (prevEntry.totalArea ?? 0) > 0 ||
        !!prevEntry.netAreaFormula ||
        !!prevEntry.totalAreaFormula
      );
      const newIsEmpty =
        (entry.amount ?? 0) === 0 &&
        !entry.amountFormula &&
        (entry.netArea ?? 0) === 0 &&
        (entry.totalArea ?? 0) === 0 &&
        !entry.netAreaFormula &&
        !entry.totalAreaFormula;
      if (prevHadValues && newIsEmpty) {
        console.error('[storage][SUSPICIOUS empty measurement overwrite]', {
          photoId: entry.photoId,
          projectId,
          prev: {
            amount: prevEntry!.amount,
            net: prevEntry!.netArea,
            tot: prevEntry!.totalArea,
            fAmount: prevEntry!.amountFormula,
            fNet: prevEntry!.netAreaFormula,
            fTot: prevEntry!.totalAreaFormula,
          },
        });
        return false;
      }

      const idx = project.measurementData.findIndex(m => m.photoId === entry.photoId);
      if (idx >= 0) {
        project.measurementData[idx] = entry;
      } else {
        project.measurementData.push(entry);
      }
      return this.saveProject(project);
    } catch (error) {
      console.error('Fehler beim Speichern der Aufmaßdaten:', error);
      return false;
    }
  }

  /**
   * Speichert den LV-Kalkulator-State (Mengen + Verknüpfungen)
   */
  static saveLvState(projectId: string, lvState: StoredLvState): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) return false;
      project.lvState = lvState;
      return this.saveProject(project);
    } catch (error) {
      console.error('Fehler beim Speichern des LV-States:', error);
      return false;
    }
  }

  /**
   * Fügt ein bereits auf dem Server gespeichertes Foto den Projekt-Metadaten hinzu.
   * Es werden keine Bilddaten mehr in localStorage abgelegt.
   */
  static addPhotoMetadata(projectId: string, photo: StoredPhoto): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) return false;
      const existingIdx = project.photos.findIndex(p => p.id === photo.id);
      const cleanPhoto: StoredPhoto = { ...photo };
      delete cleanPhoto.dataUrl;
      if (existingIdx >= 0) {
        const merged = { ...project.photos[existingIdx], ...cleanPhoto };
        // Force-clear any legacy Base64 payload that survived through the spread
        delete merged.dataUrl;
        project.photos[existingIdx] = merged;
      } else {
        project.photos.push(cleanPhoto);
      }
      return this.saveProject(project);
    } catch (error) {
      console.error('Fehler beim Speichern der Foto-Metadaten:', error);
      return false;
    }
  }

  /**
   * Entfernt Foto-Metadaten und referenzierte Aufmaße aus dem lokalen Projekt.
   */
  static removePhotoMetadata(projectId: string, photoId: string): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) return false;
      project.photos = project.photos.filter(p => p.id !== photoId);
      if (project.measurementData) {
        project.measurementData = project.measurementData.filter(m => m.photoId !== photoId);
      }
      // Expliziter Lösch-Pfad — Schrumpfen ist hier intendiert.
      return this.saveProject(project, { allowShrink: true, reason: 'remove-photo' });
    } catch (error) {
      console.error('Fehler beim Entfernen der Foto-Metadaten:', error);
      return false;
    }
  }

  /**
   * Aktualisiert die ID eines Fotos und passt referenzierte Aufmaße an.
   * Wird während der Migration zu serverseitiger Speicherung verwendet.
   */
  static remapPhotoId(projectId: string, oldId: string, newId: string): void {
    const project = this.getProject(projectId);
    if (!project) return;
    project.photos = project.photos.map(p => p.id === oldId ? { ...p, id: newId } : p);
    if (project.measurementData) {
      project.measurementData = project.measurementData.map(m => m.photoId === oldId ? { ...m, photoId: newId } : m);
    }
    this.saveProject(project);
  }

  /**
   * Speichert Analyseergebnis
   */
  static saveAnalysis(analysis: StoredAnalysis, projectId: string): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) {
        throw new Error('Projekt nicht gefunden');
      }
      
      analysis.analysisDate = new Date().toISOString();
      project.analysisResults.push(analysis);
      
      return this.saveProject(project);
    } catch (error) {
      console.error('Fehler beim Speichern der Analyse:', error);
      return false;
    }
  }

  /**
   * Löscht ein Analyseergebnis aus dem Projekt
   */
  static deleteAnalysis(projectId: string, photoId: string): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) {
        throw new Error('Projekt nicht gefunden');
      }

      const originalLength = project.analysisResults.length;
      project.analysisResults = project.analysisResults.filter(
        (a) => a.photoId !== photoId
      );

      if (project.analysisResults.length === originalLength) {
        console.warn(`Keine Analyse mit photoId "${photoId}" im Projekt gefunden`);
      }

      return this.saveProject(project);
    } catch (error) {
      console.error('Fehler beim Löschen der Analyse:', error);
      return false;
    }
  }

  /**
   * Aktualisiert Angebotsdaten
   */
  static updateQuoteData(quoteData: StoredQuote, projectId: string): boolean {
    try {
      const project = this.getProject(projectId);
      if (!project) {
        throw new Error('Projekt nicht gefunden');
      }
      
      quoteData.createdDate = new Date().toISOString();
      project.quoteData = quoteData;
      
      return this.saveProject(project);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Angebotsdaten:', error);
      return false;
    }
  }

  /**
   * Exportiert alle Projektdaten als JSON
   */
  static exportAllData(): string {
    const projects = this.getAllProjects();
    const settings = this.getSettings();
    
    return JSON.stringify({
      projects,
      settings,
      exportDate: new Date().toISOString(),
      version: '1.0'
    }, null, 2);
  }

  /**
   * Importiert Projektdaten aus JSON
   */
  static importData(jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      
      if (data.projects && Array.isArray(data.projects)) {
        localStorage.setItem(this.PROJECTS_KEY, JSON.stringify(data.projects));
      }
      
      if (data.settings) {
        localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(data.settings));
      }
      
      console.log('Daten erfolgreich importiert');
      return true;
    } catch (error) {
      console.error('Fehler beim Importieren der Daten:', error);
      return false;
    }
  }

  /**
   * Berechnet verwendeten Speicherplatz
   */
  static getStorageUsage(): { used: number; available: number; percentage: number } {
    const projects = this.getAllProjects();
    const used = JSON.stringify(projects).length;
    const available = this.MAX_STORAGE_SIZE - used;
    const percentage = (used / this.MAX_STORAGE_SIZE) * 100;
    
    return { used, available, percentage };
  }

  /**
   * Einstellungen speichern/laden
   */
  static saveSettings(settings: any): void {
    localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings));
  }

  static getSettings(): any {
    const data = localStorage.getItem(this.SETTINGS_KEY);
    return data ? JSON.parse(data) : {};
  }

  /**
   * Hilfsfunktionen
   */
  /**
   * Findet alle noch nicht migrierten Fotos (mit dataUrl) im Projekt.
   * Wird vom Migrationsschritt verwendet.
   */
  static getLegacyPhotosWithData(projectId: string): StoredPhoto[] {
    const project = this.getProject(projectId);
    if (!project) return [];
    return project.photos.filter(p => !!p.dataUrl);
  }

  private static cleanupOldProjects(projects: StoredProject[]): void {
    // Sortiert nach lastModified und entfernt die ältesten
    projects.sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
    
    // Behält nur die neuesten 10 Projekte
    const keepProjects = projects.slice(0, 10);
    localStorage.setItem(this.PROJECTS_KEY, JSON.stringify(keepProjects));
    
    console.log(`${projects.length - keepProjects.length} alte Projekte entfernt`);
  }

  /**
   * Cache für Performance
   */
  static clearCache(): void {
    localStorage.removeItem(this.PROJECTS_KEY);
    localStorage.removeItem(this.SETTINGS_KEY);
    console.log('Cache erfolgreich geleert');
  }

  /**
   * Synchronisation mit Server (für zukünftige Implementierung)
   */
  static async syncWithServer(): Promise<boolean> {
    // TODO: Server-Synchronisation implementieren
    console.log('Server-Synchronisation noch nicht implementiert');
    return false;
  }
}